import * as React from 'react';
import type { MintingDappProps } from './types';

const componentCache = new Map<string, React.ComponentType<MintingDappProps>>();

/**
 * Load a React component from JavaScript source code string.
 * Caches loaded components by dappId for performance.
 *
 * The component_code should be a JavaScript expression that evaluates to a React component.
 * The code should use React from the provided scope and return the component.
 *
 * Examples:
 *   ```javascript
 *   function MyComponent({ dappId, appKit, account, chainId }) {
 *     return React.createElement('div', null, 'Hello from dynamic component');
 *   }
 *   ```
 * Or using JSX (pre-transpiled to React.createElement):
 *   ```javascript
 *   const MyComponent = (props) => React.createElement('div', null, `Dapp: ${props.dappId}`);
 *   ```
 *
 * @param code - The JavaScript source code that evaluates to a React component
 * @param dappId - The dapp identifier (for caching)
 * @returns The React component
 * @throws If loading or evaluation fails
 */
export function loadComponentFromCode(code: string, dappId: string): React.ComponentType<MintingDappProps> {
  if (componentCache.has(dappId)) {
    return componentCache.get(dappId)!;
  }

  try {
    // Create a function that receives React as argument and returns the component
    // The code should be an expression that evaluates to the component
    const fn = new Function('React', `"use strict"; return (${code});`);
    const component = fn(React);

    if (!component) {
      throw new Error('Component code did not evaluate to a React component');
    }

    // Verify it's a function (class or functional component)
    if (typeof component !== 'function') {
      throw new Error('Loaded component is not a function');
    }

    componentCache.set(dappId, component);
    return component;
  } catch (error) {
    console.error(`[ComponentLoader] Failed to load component for dapp ${dappId}:`, error);
    throw error;
  }
}

/**
 * Clear the component cache (useful for testing or hot-reload scenarios)
 */
export function clearComponentCache(): void {
  componentCache.clear();
}

/**
 * Check if a component is cached for a given dappId
 */
export function hasCachedComponent(dappId: string): boolean {
  return componentCache.has(dappId);
}
